/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Mobx model(s) related to bookshelf
 *
 * @file Bookshelf.js
 * @author Saravanan Kandasamy
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';

import {
  BOOKSHELF_DATA_REQUESTED,
  BOOKSHELF_DATA_FETCHED,
  REMOVE_ENTITLEMENT_SUCCESS,
  ENTITLEMENTLEVEL_MULTI,
  ACTIVE,
  HERO_BANNER_TYPES,
  DIAGNOSTICS_SERVICE_REQUESTED,
  ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS
} from '../../../common/constants';
import ProductUtils from '../../../common/utils/ProductUtils';
import CommonUtils from '../../../common/utils/CommonUtils';
import Book from './Book';

/**
 * A mobx model for bookshelf
 *
 */
const Bookshelf = types.model(
  'Bookshelf',
  {
    books: types.array(Book)
  }
).views(self => ({
  /**
   * Get all books
   * @returns {Array} - All books
   */
  getBooks() {
    return getSnapshot(self.books);
  },
  /**
   * Get recently accessed books
   * @returns {Array} - Recently accessed books
   */
  getRecentlyAccessedBooks() {
    const books = self.getBooks();
    const activeBooks = CommonUtils.getActiveBooks(books);

    if (!activeBooks?.length) {
      return [];
    }

    return CommonUtils.sortByTimestampDesc(activeBooks, book => book.last_accessed_time);
  },
  /**
   * Get recently created courses
   * @returns {Array} - Recently created courses
   */
  getRecentlyCreatedCourses() {
    const books = self.getBooks();
    const activeBooks = CommonUtils.getActiveBooks(books);

    if (!activeBooks?.length) {
      return [];
    }

    return CommonUtils.sortByTimestampDesc(activeBooks, book => book.course_created_date);
  },
  /**
   * Get newly added non-course books
   * @returns {Object} - Newly added non-course books
   */
  getNewlyAddedBooks() {
    const books = self.getBooks();
    const activeBooks = CommonUtils.getActiveBooks(books);

    // Course books are excluded because the CourseWare book's product_entitlements.start_date is set to the current time on every bookshelf fetch.
    const nonCourseBooks = activeBooks.filter(book => !book.course_id);

    if (!activeBooks?.length) {
      return [];
    }

    return CommonUtils.sortByTimestampDesc(nonCourseBooks, book => book?.product_entitlements?.start_date);
  },
  /**
   * Get book by course id
   * @param {string} courseId - Course ID
   * @returns {Object} - Book
   */
  getBookByCourseId(courseId) {
    const books = self.getBooks();
    return books.find(book => book.course_id === courseId);
  },
  /**
   * Gets the available before duedate config field for the given course
   * @param {string} courseId - Course ID
   * @returns {number} - Available-before-due-days
   */
  getAvailableBeforeDueDays(courseId) {
    const book = self.getBookByCourseId(courseId);
    const entry = book?.courseConfiguration?.find(
      config => CommonUtils.localStringCompare(
        config?.key,
        ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS.AVAILABLE_BEFORE_DUE_DAYS
      )
    );
    const days = Number(entry?.value);

    return Number.isFinite(days) && days >= 0 ? days : 0;
  },
  /**
   * Gets a single assignment-settings course-configuration field for the given course
   * @param {string} courseId - Course ID
   * @param {string} field - Configuration key, e.g. ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS.DEFAULT_DUE_TIME
   * @param {*} [fallbackValue=null] - Value to return when the field is missing/empty
   * @returns {*} - The configured value (raw, caller coerces if needed), or fallbackValue
   */
  getAssignmentsSettingsConfig(courseId, field, fallbackValue = null) {
    const book = self.getBookByCourseId(courseId);
    const entry = book?.courseConfiguration?.find(
      config => CommonUtils.localStringCompare(config?.key, field)
    );
    const value = entry?.value;

    return typeof value === 'string' && value.length > 0 ? value : fallbackValue;
  }
})).actions(self => ({
  fetch() {
    Framework.getEventManager().publish(BOOKSHELF_DATA_REQUESTED, {});
  },
  refresh() {
    Framework.getEventManager().publish(BOOKSHELF_DATA_REQUESTED, { refresh: true });
  },
  set(...data) {
    applySnapshot(self, data[0]);
    if(!data[1])
    {
      Framework.getEventManager().publish(BOOKSHELF_DATA_FETCHED, {});
    }
  },
  removeEntitlement(productId, isBanner) {
    const books = self.books.filter(book => book.product_id !== productId);
    if (isBanner) {
      applySnapshot(self.bannerBook, {});
    } else {
      applySnapshot(self.books, books);
    }
    Framework.getEventManager().publish(REMOVE_ENTITLEMENT_SUCCESS);
  },
  /**
   * Mapping subscription details to book and setting product model name
   *
   */
  setSubscriptionDetails() {
    const userStore = Framework.getStoreRegistry().getStore('user');
    const user = getSnapshot(userStore);
    const books = getSnapshot(self.books).map((book) => {
      let renderBook = {};
      let gpsSubscriptionOfCurrentBook = null;
      let entitlementTierOfBook = null;
      let entitlementLevelOfBook = null;
      let subscriptionStatusOfBook = null;
      const { subscription_id: subscriptionIdOfBook } = CommonUtils.getProductEntitlementOfBook(book);
      if (subscriptionIdOfBook) {
        const {
          entitlementTier,
          status,
          entitlementLevel,
          subscriptionSource
        } = CommonUtils.getMatchingGpsSubscriptionOfBook(subscriptionIdOfBook, user);
        entitlementTierOfBook = entitlementTier;
        entitlementLevelOfBook = entitlementLevel;
        subscriptionStatusOfBook = status;
        gpsSubscriptionOfCurrentBook = {};
        gpsSubscriptionOfCurrentBook.status = status || null;
        gpsSubscriptionOfCurrentBook.entitlementLevel = entitlementLevel || null;
        gpsSubscriptionOfCurrentBook.subscriptionSource = subscriptionSource || null;
      }
      const productModelName = ProductUtils.getProductModel(book.product_entitlements.business_model_code, entitlementTierOfBook, false);
      const format = ProductUtils.getProductTypeFormat(book.book_type);
      const productLabel = ProductUtils.getProductLabel(book.product_entitlements.business_model_code);
      renderBook = {
        ...book,
        product_model_name: `${productModelName}, ${format}`,
        product_label: productLabel,
        gpsSubscriptionOfBook: gpsSubscriptionOfCurrentBook
      };
      const isActiveBook = CommonUtils.isActiveBook(renderBook);
      return {
        ...renderBook,
        showAudio: book.showAudio,
        showBookmark: true,
        showFlashcard: true,
        showRemoveEntitlement: ((entitlementLevelOfBook === ENTITLEMENTLEVEL_MULTI)
          && (subscriptionStatusOfBook === ACTIVE) && isActiveBook)
      };
    });

    applySnapshot(self.books, books);
  },
  /**
   * Archives or unarchives selected books
   *
   * @param {Array} selectedBookIds
   * @param {*} isArchived
   */
  archiveOrUnarchiveSelectedBooks(selectedBookIds = [], isArchived = false) {
    let heroBookId = null;
    const heroBannerStore = Framework.getStoreRegistry().getStore('herobanner');
    const { titleInfo } = getSnapshot(heroBannerStore);
    const books = getSnapshot(self)?.books;
    const updatedBooks = books.map((book) => {
      if (titleInfo?.book_id === book.book_id
        && titleInfo?.product_id === book.product_id
        && titleInfo?.course_id === book.course_id) {
        heroBookId = book.book_id;
      }
      if (selectedBookIds.includes(book.book_id) && book.isArchived !== isArchived) {
        return {
          ...book,
          isArchived
        };
      }

      return { ...book };
    });

    if (updatedBooks.length > 0) {
      applySnapshot(self.books, updatedBooks);
      const { BOOK } = HERO_BANNER_TYPES;
      const fetchChannels = selectedBookIds.includes(heroBookId) || heroBannerStore.bannerType !== BOOK;
      this.setShowArchiveOrUnarchive(true);
    }
  },
  setShowArchiveOrUnarchive(enableTitleArchival) {
    const { books } = getSnapshot(self);

    const updatedBooks = books.map(book => ({
      ...book,
      showArchive: enableTitleArchival && !book.isArchived,
      showUnArchive: enableTitleArchival && book.isArchived
    }));
    applySnapshot(self.books, updatedBooks);
  },
  fetchDiagnosticsInfo(data) {
    const eventData = {
      eventType: 'GET',
      data
    }
    Framework.getEventManager().publish(DIAGNOSTICS_SERVICE_REQUESTED, eventData);
  },
  setDiagnosticsInfo(diagnosticsInfo, courseRegistrarId) {
    const { books } = getSnapshot(self);
    const updatedBooks = books.map(book => ({
      ...book,
      diagnosticsInfo:  CommonUtils.handleLMSBookMatchByCourseId(book, courseRegistrarId)
        ? diagnosticsInfo
        : null
    }));
    applySnapshot(self.books, updatedBooks);
  },
  setShowDiagnosticsLink(courseRegistrarId) {
    const { books } = getSnapshot(self);
    const updatedBooks = books.map(book => ({
      ...book,
      showRunDiagnostics: CommonUtils.handleLMSBookMatchByCourseId(book, courseRegistrarId)
    }));
    const hasChanges = updatedBooks.some((book, index) => (
      book.showRunDiagnostics !== books[index]?.showRunDiagnostics
    ));

    if (!hasChanges) {
      return;
    }
    applySnapshot(self.books, updatedBooks);
  },
  // eslint-disable-next-line camelcase
  setLastAccessBook({ book_id, course_id, product_id }) {
    const { books } = getSnapshot(self);

    const renderBooks = books.map((book) => {
      const renderBook = { ...book };
      // eslint-disable-next-line camelcase
      if (book.book_id === book_id && book.course_id === course_id && book.product_id === product_id) {
        renderBook.last_accessed_time = new Date().getTime();
      }

      return renderBook;
    });

    applySnapshot(self.books, renderBooks);
  },
  setError() {
  },
  /**
   * Set user book preference
   *
   * @param {Object} userBookPreference - User book preference
   * @param {string} courseId - Course ID
   * @param {string} bookId - Book ID
   * @param {string} productId - Product ID
   * @returns {void}
   */
  setUserBookPreference(userBookPreference, courseId, bookId, productId) {
    if (!userBookPreference || CommonUtils.isEmpty(userBookPreference) || self?.books?.length === 0) {
      return;
    }

    const currentBook = self.books.find(book => (
      book.book_id === bookId
        && book.product_id === productId
        && book.course_id === courseId
    ));

    if (!currentBook) {
      return;
    }

    currentBook.userBookPreference = userBookPreference;
  },
  /**
   * Set course configuration
   *
   * @param {Object} data - Course configuration data
   * @param {string} courseId - Course ID
   * @returns {void}
   */
  setCourseConfiguration(data = {}, courseId) {
    const { configurations } = data;
    if (!courseId || !configurations || self?.books?.length === 0) {
      return;
    }

    const currentBook = self.books.find(book => book.course_id === courseId);

    if (!currentBook) {
      return;
    }

    currentBook.courseConfiguration = configurations;
  },
  /**
   * Set course configuration
   *
   * @param {Object} data - Course configuration data
   * @param {string} courseId - Course ID
   * @returns {void}
   */
  setCourseSettings(data = {}, courseId) {
    const { settings } = data;

    if (!courseId || !settings || self?.books?.length === 0) {
      return;
    }

    const currentBook = self.books.find(book => book.course_id === courseId);

    if (!currentBook) {
      return;
    }

    currentBook.courseSettings = settings;
  },
  /**
   * Set instructor controls from API configurations
   *
   * @param {Array} configurations - Instructor configuration entries
   * @returns {void}
   */
  setInstructorControls(configurations = []) {
    configurations.forEach(({ courseId, controls }) => {
      const book = self.books.find(b => b.course_id === courseId);

      if (book) {
        const isTACourse = CommonUtils.isTACourse(book);

        book.controls = {
          ...controls,
          createCourse: isTACourse ? false : controls.createCourse,
          createCopyCourse: isTACourse ? false : controls.createCopyCourse
        };
      }
    });
  },
  /**
   * Marks the course as no longer LMS linked after a successful unlink
   *
   * @param {string} courseId
   * @returns {void}
   */
  setCourseUnlinkedFromLMS(courseId) {
    self.books.forEach((book) => {
      if (book.course_id !== courseId) {
        return;
      }

      book.isLms = false;

      const course = book.courses?.find(c => c.course_id === courseId);

      if (course) {
        course.isLms = false;
      }
    });
  },
  /**
   * Updates the location for the book
   *
   * @param {Object} book
   * @returns {void}
   */
  updateLocation(book = {}) {
    const books = self.books;

    if (!books?.length) {
      return;
    }

    const currentBook = books.find(
      b => b.course_id === book.course_id && b.book_id === book.book_id && b.product_id === book.product_id
    );

    if (!currentBook) {
      return;
    }

    currentBook.last_accessed_time = new Date().getTime();
  },
  setPrintCompanion(book = {}, value = true, printOfferData = {}) {
    const { books } = getSnapshot(self);

    if (!books?.length || !book?.product_id) {
      return;
    }

    const updatedBooks = books.map(b => (
      (b.product_id === book.product_id && b.course_id)
        ? { ...b, showPrintCompanion: value, printOffer: printOfferData }
        : b
    ));

    const hasChanges = updatedBooks.some((b, index) => (
      b.showPrintCompanion !== books[index]?.showPrintCompanion
    ));

    if (!hasChanges) {
      return;
    }

    applySnapshot(self.books, updatedBooks);
  },
  /**
   * Set channels access
   *
   * @param {Object} channelsAccessData - Channels access data
   * @returns {void}
   */
  setChannelsAccess(channelsAccessData = {}) {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook();

    const { ...channelsAccess } = channelsAccessData;
    const {
      channel,
      status,
      matchType
    } = channelsAccess;
    const bookToUpdate = self.books.find(book => (
      book.book_id === heroBook?.book_id
      && book.course_id === heroBook?.course_id
      && book.product_id === heroBook?.product_id
    ));

    if (!bookToUpdate) {
      return;
    }

    const isMlm = CommonUtils.isIntegratedBMC(heroBook?.product_entitlements?.business_model_code);
    const mappedAccess = CommonUtils.mapChannelsAccess({
      channel,
      status,
      matchType,
      courseId: heroBook?.course_id,
      isMlm
    });

    bookToUpdate.channel_id = mappedAccess.channel_id;
    bookToUpdate.channel_mapping_type = mappedAccess.channel_mapping_type;
    bookToUpdate.channel_name = mappedAccess.channel_name;
    bookToUpdate.hasAccessToExactOrLiteMatch = mappedAccess.hasAccessToExactOrLiteMatch;
  },
  /**
   * Remove channel access
   *
   * @param {Object} channelsAccessData - Channels access data
   * @returns {void}
   */
  removeChannelAccess() {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook();
    const bookToUpdate = self.books.find(book => (
      book.book_id === heroBook?.book_id
      && book.course_id === heroBook?.course_id
      && book.product_id === heroBook?.product_id
    ));

    if (!bookToUpdate) {
      return;
    }

    bookToUpdate.channel_id = null;
    bookToUpdate.channel_mapping_type = null;
    bookToUpdate.channel_name = null;
    bookToUpdate.hasAccessToExactOrLiteMatch = false;
  }
}));

export default Bookshelf;
